import { useGetTestMeterByIdQuery } from '@/store/sat/satApi';
import { FC, useState } from 'react';
import BaseModal from '@/components/customUI/Modals';
import Eye from '@/components/svg/Eye';
import {
  DialogDescription,
  DialogTitle,
  DialogTriggerProps
} from '@radix-ui/react-dialog';

import Spinner from '@/components/customUI/Loaders/Spinner';
import CommandResponseTable from '@/pages/hes/command/execution-history/includes/CommandDetailsModal/CommandResponseTable';
import { groupEventDataByDataType } from '@/pages/hes/command/includes/utils';
import { TestByIdMeters } from '@/store/sat/types/records/tests';
import { formatDate } from '@/lib/utils/dateTime';
import {
  toggleObis,
  ToggleObisRecord
} from '@/pages/hes/command/execution-history/includes/CommandDetailsModal/lib';

type TestCommandResponseModalProps = {
  commandName: string;
  data: TestByIdMeters;
};

const TestCommandResponseModal: FC<TestCommandResponseModalProps> = ({
  commandName,
  data: meterData
}) => {
  const [open, setOpen] = useState(false);
  const { data, isFetching } = useGetTestMeterByIdQuery(
    { id: String(meterData.id) },
    {
      skip: !open
    }
  );
  const title = `Meter: ${
    meterData.deviceIdentifier ?? meterData.deviceSerial
  }, Command: ${commandName}, Execution: ${formatDate(
    meterData.executionStartTime as string,
    'DD-MM-YYYY HH:mm:ss'
  )}`;
  const response = data ? (data as TestByIdMeters[]) : [];
  const record =
    response &&
    Array.isArray(response[0]?.responseData) &&
    response[0].responseData.length > 0 &&
    Object.keys(response[0].responseData[0]).length > 0
      ? response[0].responseData.map((e) => ({
          cmd_name: commandName,
          payload: e.responseData || {}
        }))
      : [];

  const obisCode = Object.keys(toggleObis).includes(commandName)
    ? toggleObis[commandName as keyof ToggleObisRecord]
    : '';
  const newData = groupEventDataByDataType(record || [], commandName);

  const buttonProps: Partial<DialogTriggerProps> =
    import.meta.env.VITE_IN_TESTING === 'true'
      ? { id: response[0]?.clientRequestId || '' }
      : {};

  return (
    <BaseModal
      open={open}
      buttonProps={buttonProps}
      setOpen={setOpen}
      modalClass="max-w-[80vw] max-h-[80vh] min-h-[40vh] py-0 flex flex-col"
      ButtonLogo={Eye}>
      <DialogTitle
        id="command-details-modal"
        className="font-semibold text-xl mt-5 text-[#0A3690]">
        Command Response Payload
      </DialogTitle>
      <DialogDescription>
        <span className="text-[#708CC7] text-sm">{title}</span>{' '}
        {Object.keys(toggleObis).includes(commandName) && (
          <span className="text-[#708CC7] text-sm">
            , OBIS Code:
            {obisCode}
          </span>
        )}
      </DialogDescription>
      <div className="overflow-y-scroll w-full p-2 mb-2 flex flex-col">
        <div className="flex flex-col gap-3">
          {!isFetching ? (
            <CommandResponseTable
              isFailedCommnad={false}
              parent="sat"
              title=""
              commandName={commandName}
              data={newData}
            />
          ) : (
            <div className="min-h-[40vh] flex items-center justify-center">
              <Spinner />
            </div>
          )}
        </div>
      </div>
    </BaseModal>
  );
};

export default TestCommandResponseModal;
